import { useState } from 'react'

/**
 * @typedef {Object} AlertBannerProps
 * @property {string} message - The message to display in the alert banner.
 * @property {string} confirmText - The text for the confirm button.
 * @property {string} dismissText - The text for the dismiss button.
 * @property {function} onConfirm - The function to call when the confirm button is clicked.
 * @property {function} onDismiss - The function to call when the dismiss button is clicked.
 * @property {boolean} open - The state indicating whether the banner is open.
 * @property {function} setOpen - The function to set the open state of the banner.
 * @property {Object} rest - Additional properties to be spread onto the alert banner.
 */

/**
 * Custom hook to manage the state and properties of an alert banner.
 *
 * @param {Object} params - The parameters object.
 * @param {string} params.message - The message to display in the alert banner.
 * @param {string} params.confirmText - The text for the confirm button.
 * @param {string} params.dismissText - The text for the dismiss button.
 * @param {Object} rest - Additional properties to be spread onto the alert banner.
 * @returns {AlertBannerProps} An object containing the properties for the alert banner.
 */
export default function useAlertBanner({ message, confirmText, dismissText, ...rest }) {
  const [open, setOpen] = useState(true)

  return {
    message,
    confirmText,
    dismissText,
    onConfirm: () => setOpen(false),
    onDismiss: () => setOpen(false),
    open,
    setOpen,
    ...rest,
  }
}
